import type { ThemeRecipe } from './recipe';
import type {
  CelebrationSpec,
  CustomTheme,
  CustomThemeOrigin,
  SoundSpec,
  TapEffectSpec,
  Theme,
} from './types';

// Shared source of truth for the "new custom theme" defaults. Consumed by
// the v3 → v4 storage migration (which lifts a legacy inline
// `profile.theme.custom` recipe into a library entry with these defaults
// filled in) and by tasks/47's "+ Uusi teema" editor path. Keeping both
// paths on one factory means a v3 tree migrated today produces the same
// shape a fresh #47 create would, so we never grow two "default custom
// theme" flavours.

// Neutral shape-preserving defaults. Effect runtimes land in tasks/47; the
// values here are the schema-forward defaults, not tuned experiences.
export const DEFAULT_TAP_EFFECT: TapEffectSpec = {
  variants: [{ kind: 'fly-to-counter' }],
  pickMode: 'sequential',
};

export const DEFAULT_CELEBRATION: CelebrationSpec = {
  variants: [{ kind: 'confetti' }],
  pickMode: 'random',
};

export const DEFAULT_SOUND: SoundSpec = {
  tap: {
    variants: [{ kind: 'chord', notes: ['C5', 'E5', 'G5'] }],
    pickMode: 'sequential',
  },
  celebration: {
    variants: [{ kind: 'chord', notes: ['C5', 'E5', 'G5', 'C6'] }],
    pickMode: 'sequential',
  },
};

// Blank starting recipe — a pleasant sky blue so a new theme is never a
// harsh flat white while the parent picks colours.
const BLANK_RECIPE: ThemeRecipe = { base: '#52c5f0', mood: 'bright' };

interface BuildOptions {
  origin?: CustomThemeOrigin;
  recipe?: ThemeRecipe;
  paletteOverride?: string[];
}

// Build a `CustomTheme` with the schema-forward defaults filled in. The
// caller supplies the identity (id + name) and optionally a recipe or
// palette override; everything else comes from the module constants so
// the migration and #47's create-new both stay aligned.
export function buildBlankCustomTheme(
  id: string,
  name: string,
  now: string = new Date().toISOString(),
  options: BuildOptions = {},
): CustomTheme {
  const recipe = options.recipe ?? BLANK_RECIPE;
  const paletteMode = options.paletteOverride ? 'manual' : 'derived';
  const base: CustomTheme = {
    id,
    name,
    origin: options.origin ?? { kind: 'blank' },
    recipe,
    paletteMode,
    buttonColorMode: 'palette',
    decoration: { kind: 'off' },
    tapEffect: DEFAULT_TAP_EFFECT,
    celebration: DEFAULT_CELEBRATION,
    sound: DEFAULT_SOUND,
    createdAt: now,
    updatedAt: now,
  };
  return options.paletteOverride ? { ...base, paletteOverride: options.paletteOverride } : base;
}

// Best-effort seed from a preset — pulls the accent colour into the
// recipe base, the routineCardPalette into a manual override, and marks
// origin as duplicated so the editor can render "kopio teemasta X". The
// resulting theme approximates the preset visually; the parent tweaks
// from there.
export function buildCustomThemeFromPreset(
  preset: Theme,
  id: string,
  name: string,
  now: string = new Date().toISOString(),
): CustomTheme {
  return buildBlankCustomTheme(id, name, now, {
    origin: { kind: 'duplicated', from: preset.key },
    recipe: { base: preset.tokens.accent, mood: 'bright' },
    paletteOverride: preset.routineCardPalette,
  });
}
